import React, { useState, useCallback, useEffect } from 'react';
import { useMatch, useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { validateAddEditForm } from './validations';
import Header from './components/Header';
import Buttons from './components/Buttons';
import ProductDetailsTab from './components/ProductDetailsTab';
import useFetch from '../../../../hooks/useFetch';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import { AddEdit as AddEditInterface, AddEditApi } from '../../../../interfaces/product.interface';
import { Validation } from '../../../../interfaces/common';
import ProductService from '../../../../services/product.service';
import ProductDivisionService from '../../../../services/productDivision.service';
import { Modules, AuthRoutes, RouteParamsTypes } from '../../../../enums';
import Spinner from '../../../../components/Spinner';
import Toast from '../../../../components/Toast';

const initialFormData: AddEditInterface = {
  name: '',
  description: '',
  product_division_id: '',
  status: 'true',
};

const initialValidation: Validation = { validate: true, fields: [] };

const ProductAddEditView: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { id } = useParams();
  const view = !!useMatch(Modules.products + AuthRoutes.products + RouteParamsTypes.view + '/:id');
  const edit = !!useMatch(Modules.products + AuthRoutes.products + RouteParamsTypes.edit + '/:id');
  const navigate = useNavigate();

  const [formData, setFormData] = useState<AddEditInterface>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [actionTwoLoading, setActionTwoLoading] = useState<boolean>(false);
  const [validation, setValidation] = useState<Validation>(initialValidation);
  const [divisionSearchText, setDivisionSearchText] = useState('');

  useEffect(() => {
    setFormData(initialFormData);
    setValidation(initialValidation);
    setDivisionSearchText('');
  }, [location.key]);

  // Fetch existing product if editing/viewing
  const { loading: getContentLoading } = useFetch({
    service: ProductService,
    func: 'get',
    payload: id,
    dependency: [id, location.key],
    skip: !id,
    onSuccess: async (response: any) => {
      setFormData({
        id,
        name: response.name,
        description: response.description,
        product_division_id: response.product_division?.id ? String(response.product_division.id) : '',
        status: String(response.is_active),
      });
    },
    onError: (error: any) => { },
  });

  // All divisions for the dropdown (pre-loaded)
  const { data: allDivisionsData } = useFetch({
    service: ProductDivisionService,
    func: 'lookup',
    payload: { searchText: '' },
    dependency: [],
  });

  // Searched divisions (when user types in dropdown search)
  const { data: searchedDivisionsData } = useFetch({
    service: ProductDivisionService,
    func: 'lookup',
    payload: { searchText: divisionSearchText },
    dependency: [divisionSearchText],
    skip: !divisionSearchText,
  });

  const extractDivisions = (data: any): { label: string; value: string }[] => {
    const arr = Array.isArray(data) ? data : data?.data || [];
    return arr.map((item: any) => ({ label: item.name, value: String(item.id) }));
  };

  const allDivisionOptions = extractDivisions(allDivisionsData);
  const divisionOptions = divisionSearchText
    ? extractDivisions(searchedDivisionsData)
    : allDivisionOptions;

  const handleInputChange = useCallback(
    (event: any, name: string) => {
      let value: any = event.target.value;
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    [setFormData]
  );

  const handleInputBlur = useCallback(
    (event: any, name: string) => {
      let value = typeof event.target.value === 'string' ? event.target.value.trim() : event.target.value;
      let newValidation = validateAddEditForm({ ...formData, [name]: value }, name);
      setValidation(newValidation);
    },
    [formData]
  );

  const handleDivisionSearch = useCallback((event: any, _name: string) => {
    setDivisionSearchText(event.target.value);
  }, []);

  const handleSubmit = useCallback(
    async (action: '1' | '2', event?: React.FormEvent) => {
      event && event.preventDefault();

      const newValidation = validateAddEditForm(formData);
      setValidation(newValidation);
      if (!newValidation.validate) return;

      if (action === '1') setLoading(true);
      else setActionTwoLoading(true);

      try {
        const payload: AddEditApi = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          product_division_id: Number(formData.product_division_id),
          is_active: formData.status === 'true',
        };

        let response: any;
        if (!edit) {
          response = await ProductService.add(payload);
        } else if (id) {
          response = await ProductService.edit(id, payload);
        }

        if (response.success !== false) {
          toast.success(
            (
              <Toast
                message={
                  edit
                    ? t('products.addEdit.pageTexts.form.editSuccess')
                    : t('products.addEdit.pageTexts.form.addSuccess')
                }
              />
            ) as any,
            { icon: false }
          );

          if (edit) {
            navigate(Modules.products + AuthRoutes.products);
          } else if (action === '2') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            const input = document.getElementById(VALIDATION_FIELD_MAPPING.name);
            input && input.focus();
            setFormData(initialFormData);
            setValidation(initialValidation);
            setDivisionSearchText('');
          } else {
            navigate(`${Modules.products}${AuthRoutes.products}`, { replace: true });
          }
        }

        if (action === '1') setLoading(false);
        else setActionTwoLoading(false);
      } catch (err: any) {
        if (action === '1') setLoading(false);
        else setActionTwoLoading(false);
      }
    },
    [id, edit, formData, navigate, t]
  );

  const handleKeyDown = useCallback(
    (e: any, name?: string) => {
      if (e.key === 'Enter') handleSubmit('1');
    },
    [handleSubmit]
  );

  const handleCancel = () => {
    navigate(Modules.products + AuthRoutes.products);
  };

  let formContent;

  if (getContentLoading) {
    formContent = (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  } else {
    formContent = (
      <ProductDetailsTab
        edit={edit}
        view={view}
        formData={formData}
        validation={validation}
        divisionOptions={divisionOptions}
        allDivisionOptions={allDivisionOptions}
        divisionSearchText={divisionSearchText}
        onKeyDown={handleKeyDown}
        onInputBlur={handleInputBlur}
        onInputChange={handleInputChange}
        onDivisionSearch={handleDivisionSearch}
      />
    );
  }

  return (
    <>
      <Header id={id} view={view} edit={edit} />

      <div className="card mb-6">
        <h5 className="card-header">
          {!id
            ? t('products.addEdit.pageTexts.title.addTitle')
            : view
              ? t('products.addEdit.pageTexts.title.viewTitle')
              : t('products.addEdit.pageTexts.title.editTitle')}
        </h5>
        <hr className="m-0"></hr>

        {formContent}

        <hr className="m-0"></hr>
        <Buttons
          actionTwoLoading={actionTwoLoading}
          loading={loading}
          view={view}
          edit={edit}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </div>
    </>
  );
};

export default ProductAddEditView;
